// Liquid-glass slash-command picker. Anchored to the top of the
// composer; opens when the user types `/` in the textarea, filters
// as they type, closes on Enter / click / Escape / blur.
//
// The Composer owns slash state (open + query + selectedIndex) and
// passes them in. This component is purely presentational.

import { useEffect, useRef } from "react";
import type { SkillDescriptor } from "../lib/skills";

interface Props {
  open: boolean;
  query: string;
  filteredSkills: SkillDescriptor[];
  selectedIndex: number;
  onSelect: (skill: SkillDescriptor) => void;
  onHover: (index: number) => void;
}

export default function SlashMenu({
  open,
  query,
  filteredSkills,
  selectedIndex,
  onSelect,
  onHover,
}: Props) {
  const listRef = useRef<HTMLDivElement>(null);

  // Scroll selected into view when keyboard nav moves it.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (!el) return;
    const row = el.querySelector<HTMLElement>(`[data-idx="${selectedIndex}"]`);
    if (row) row.scrollIntoView({ block: "nearest" });
  }, [open, selectedIndex]);

  if (!open) return null;

  // Group skills by group field for visual separation.
  type Group = { label: string; items: { skill: SkillDescriptor; flatIdx: number }[] };
  const groups: Group[] = [];
  filteredSkills.forEach((skill, flatIdx) => {
    const last = groups[groups.length - 1];
    if (last && last.label === skill.group) {
      last.items.push({ skill, flatIdx });
    } else {
      groups.push({ label: skill.group, items: [{ skill, flatIdx }] });
    }
  });

  return (
    <div className="slashMenu" role="listbox" aria-label="Slash commands">
      <div className="slashMenu__header">
        <span className="slashMenu__title">Skills</span>
        <span className="slashMenu__hint">↑↓ navigate · ↵ select · esc close</span>
      </div>
      <div className="slashMenu__list" ref={listRef}>
        {filteredSkills.length === 0 ? (
          <div className="slashMenu__empty">
            No skills match <code>/{query}</code>
          </div>
        ) : (
          // Key by the group's first flat index, NOT by its label. `groups` is
          // built from CONSECUTIVE runs above, so one label legitimately recurs
          // many times in a single list ("Client work" a dozen times, etc.).
          // Duplicate keys among siblings leave React unable to match children
          // across renders: it keeps stale group nodes and appends the new ones,
          // so a fresh query's results pile up underneath the previous list
          // instead of replacing it. The first flatIdx is unique per group by
          // construction, and changes when the results change.
          groups.map((g) => (
            <div key={`${g.label}:${g.items[0]!.flatIdx}`} className="slashMenu__group">
              <div className="slashMenu__groupLabel">{g.label}</div>
              {g.items.map(({ skill, flatIdx }) => {
                const active = flatIdx === selectedIndex;
                return (
                  <button
                    key={skill.name}
                    type="button"
                    role="option"
                    aria-selected={active}
                    data-idx={flatIdx}
                    className={`slashMenu__item ${active ? "is-active" : ""}`}
                    onClick={() => onSelect(skill)}
                    onMouseEnter={() => onHover(flatIdx)}
                  >
                    <span className="slashMenu__itemName">
                      <span className="slashMenu__itemSlash">/</span>
                      {skill.name}
                    </span>
                    <span className="slashMenu__itemSummary">{skill.summary}</span>
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
